import { useState, useRef, useEffect } from 'react';
import { ChevronDown, LayoutGrid, AlertTriangle } from 'lucide-react';
import { REPLICAS } from '../lib/replicas.config';
import { navigate } from '../lib/routing';

export function ReplicaSwitcher({ replica, isAdmin = false, scopes = [] }) {
  // Admin ve tudo (Principal + todas as replicas). Usuario normal so ve
  // o que esta no app_access.scopes.
  const visibleReplicas = isAdmin
    ? Object.entries(REPLICAS)
    : Object.entries(REPLICAS).filter(([slug]) => scopes.includes(slug));
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const goPrincipal = () => { setOpen(false); navigate('/'); };
  const goReplica = (slug) => { setOpen(false); navigate(`/${slug}`); };

  // Texto do botao (estado atual)
  let currentLabel = 'Principal';
  let currentSub = 'Todas as empresas';
  if (replica?.isReplica) {
    currentLabel = replica.config?.label || replica.slug;
    currentSub = replica.empresa
      ? (replica.empresa.nome_fantasia || replica.empresa.razao_social)
      : 'empresa não configurada';
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '8px 14px', borderRadius: 'var(--radius)',
          border: replica?.misconfigured
            ? '1px solid rgba(255,170,51,0.4)'
            : '1px solid var(--border-2)',
          background: replica?.misconfigured ? 'var(--warning-soft)' : 'var(--surface-2)',
          color: 'var(--ink)',
          cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5,
          transition: 'all .15s ease',
        }}
        onMouseOver={e => {
          e.currentTarget.style.borderColor = 'var(--primary)';
        }}
        onMouseOut={e => {
          e.currentTarget.style.borderColor = replica?.misconfigured
            ? 'rgba(255,170,51,0.4)'
            : 'var(--border-2)';
        }}
        title="Trocar réplica"
      >
        {replica?.misconfigured
          ? <AlertTriangle size={14} style={{ color: 'var(--warning)' }} />
          : <LayoutGrid size={14} style={{ color: 'var(--primary)' }} />}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '.08em',
            textTransform: 'uppercase', color: 'var(--muted)',
          }}>
            {replica?.isReplica ? 'Réplica' : 'Modo'}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{currentLabel}</span>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{currentSub}</span>
        </div>
        <ChevronDown size={14} style={{ marginLeft: 6, color: 'var(--muted)' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 6,
          width: 280, zIndex: 50,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
          padding: 6,
        }}>
          {/* Principal so aparece pra admin — usuario normal nao tem
              acesso ao modo geral, so as replicas que ele esta liberado. */}
          {isAdmin && (
            <>
              <Item
                active={!replica?.isReplica}
                label="Principal"
                sub="Todas as empresas (admin)"
                icon={LayoutGrid}
                onClick={goPrincipal}
              />
              <div className="divider" style={{ margin: '4px 0' }} />
            </>
          )}
          {visibleReplicas.map(([slug, cfg]) => {
            const isActive = replica?.slug === slug;
            const sub = cfg.empresaNome
              ? cfg.empresaNome
              : 'sem empresa configurada';
            return (
              <Item
                key={slug}
                active={isActive}
                label={cfg.label || slug}
                sub={sub}
                muted={!cfg.empresaNome}
                onClick={() => goReplica(slug)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function Item({ label, sub, icon: Icon, active, muted, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 8, border: 'none',
        background: active ? 'var(--primary-soft)' : 'transparent',
        color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit',
        textAlign: 'left',
      }}
      onMouseOver={e => { if (!active) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseOut={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {Icon && <Icon size={14} style={{ color: active ? 'var(--primary)' : 'var(--muted)', flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: active ? 'var(--primary)' : 'var(--ink)',
        }}>{label}</div>
        <div style={{
          fontSize: 11.5,
          color: muted ? 'var(--muted-2)' : 'var(--muted)',
          fontStyle: muted ? 'italic' : 'normal',
        }}>{sub}</div>
      </div>
    </button>
  );
}
